import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useParentContext, type ParentChild } from "@/lib/parent-context";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/parent/timetable")({
  ssr: false,
  beforeLoad: () => requireRole("parent"),
  head: () => ({ meta: [{ title: "Timetable — MAKAI" }] }),
  component: TimetablePage,
});

const DAYS = ["mon","tue","wed","thu","fri","sat"] as const;
const DAY_LABEL: Record<string, string> = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

interface Slot { id: string; day: string; start: string; end: string; subject: string; room: string | null }

function TimetablePage() {
  const { ctx } = useParentContext();
  const [child, setChild] = useState<ParentChild | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);

  useEffect(() => { if (ctx && !child) setChild(ctx.children[0] ?? null); }, [ctx, child]);

  useEffect(() => {
    if (!child?.streamId) { setSlots([]); return; }
    let cancelled = false;
    (async () => {
      const { data: ts } = await supabase
        .from("timetable_slots")
        .select("id, day, start_time, end_time, room, subject_id")
        .eq("stream_id", child.streamId!)
        .order("start_time");
      const subjectIds = Array.from(new Set((ts ?? []).map((t) => t.subject_id).filter(Boolean) as string[]));
      const map: Record<string, string> = {};
      if (subjectIds.length) {
        const { data: subj } = await supabase.from("subjects_catalog").select("id, name").in("id", subjectIds);
        for (const s of subj ?? []) map[s.id] = s.name;
      }
      if (cancelled) return;
      setSlots((ts ?? []).map((t) => ({
        id: t.id, day: t.day, start: t.start_time, end: t.end_time, room: t.room ?? null,
        subject: t.subject_id ? map[t.subject_id] ?? "Class" : "Class",
      })));
    })();
    return () => { cancelled = true; };
  }, [child]);

  const byDay = useMemo(() => {
    const out: Record<string, Slot[]> = {};
    for (const d of DAYS) out[d] = [];
    for (const s of slots) (out[s.day] ??= []).push(s);
    return out;
  }, [slots]);

  return (
    <AppShell role="parent" title="Weekly timetable" subtitle="See exactly what your child is studying each day.">
      {ctx && ctx.children.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {ctx.children.map((c) => (
            <button key={c.learnerId} type="button" onClick={() => setChild(c)}
              className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold",
                child?.learnerId === c.learnerId ? "border-primary bg-primary-soft text-primary" : "border-border bg-card")}>{c.firstName}</button>
          ))}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {DAYS.map((d) => (
          <article key={d} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
            <h3 className="mb-2 font-display text-sm font-bold">{DAY_LABEL[d]}</h3>
            {byDay[d].length === 0 ? (
              <p className="text-xs text-muted-foreground">No classes.</p>
            ) : (
              <ul className="space-y-1.5">
                {byDay[d].map((s) => (
                  <li key={s.id} className="grid grid-cols-[auto_minmax(0,1fr)] gap-2 rounded-lg bg-muted/50 px-2.5 py-1.5">
                    <span className="text-[11px] font-semibold text-primary">{s.start.slice(0,5)}</span>
                    <span className="truncate text-xs font-medium">{s.subject}{s.room ? <span className="ml-1 text-muted-foreground">· {s.room}</span> : null}</span>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>
    </AppShell>
  );
}
